import { useState, useEffect, FormEvent } from 'react';
import { getGallery, createAlbum, updateAlbum, deleteAlbum, deleteUploads, Album } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import ImageUploader from '../components/ImageUploader';
import './Gallery.css';
import { overlayClose } from '../utils/overlayClose';

function Gallery() {
  const { plan } = useAuth();

  const [albums, setAlbums] = useState<Album[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState<Album | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [albumLimit, setAlbumLimit] = useState<number | null>(null);
  const [imageLimit, setImageLimit] = useState<number | null>(null);
  const [hiddenCount, setHiddenCount] = useState(0);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    coverImage: '',
    images: [] as string[],
  });

  // Snapshot of `images` at modal-open time. Diffing formData.images
  // against this gives us the "uploaded this session" list — what we
  // need to clean up from S3 if the user closes without saving. Without
  // this, photos uploaded into a Create-Album or Edit-Album modal
  // would become orphans whenever the user cancelled. See the 2026-05-30
  // kenningwell incident for the failure mode this prevents.
  const [openSnapshotImages, setOpenSnapshotImages] = useState<string[]>([]);

  const atLimit = albumLimit !== null && albums.length >= albumLimit;

  useEffect(() => {
    loadAlbums();
  }, []);

  const loadAlbums = async () => {
    try {
      setIsLoading(true);
      const data = await getGallery();
      setAlbums(data.albums);
      if (data.albumLimit !== undefined) setAlbumLimit(data.albumLimit);
      if (data.imageLimit !== undefined) setImageLimit(data.imageLimit);
      setHiddenCount(data.hiddenCount || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load albums');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (album?: Album) => {
    const initialImages = album ? [...album.images] : [];
    if (album) {
      setEditingAlbum(album);
      setFormData({
        title: album.title,
        description: album.description,
        coverImage: album.coverImage,
        images: initialImages,
      });
    } else {
      setEditingAlbum(null);
      setFormData({ title: '', description: '', coverImage: '', images: [] });
    }
    setOpenSnapshotImages(initialImages);
    setError('');
    setShowModal(true);
  };

  /**
   * Compute the images that were uploaded inside the current modal
   * session but never persisted to the album record (i.e. user is
   * about to close without saving). These need to be deleted from S3.
   */
  const computeOrphanUrls = (): string[] => {
    const snapshotSet = new Set(openSnapshotImages);
    return formData.images.filter(u => !snapshotSet.has(u));
  };

  const handleCloseModal = async () => {
    const orphans = computeOrphanUrls();
    if (orphans.length > 0) {
      const ok = confirm(
        `Discard ${orphans.length} photo${orphans.length === 1 ? '' : 's'} you just uploaded? ` +
        `${orphans.length === 1 ? 'It will be' : 'They will be'} deleted permanently.`,
      );
      if (!ok) return;  // user changed mind; keep modal open
      // Fire-and-forget the S3 cleanup. We don't await + block the
      // close UX — the worst case is some orphans linger if the
      // request fails, and storage drifts slightly. Not customer-facing.
      deleteUploads(orphans).catch(err => {
        console.warn('[Gallery] Orphan cleanup failed:', err);
      });
    }
    setShowModal(false);
    setEditingAlbum(null);
    setOpenSnapshotImages([]);
    setError('');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsSaving(true);

    try {
      if (editingAlbum) {
        await updateAlbum(editingAlbum.albumId, {
          title: formData.title,
          description: formData.description,
          coverImage: formData.coverImage,
          images: formData.images,
        });
        setSuccessMessage('Album updated successfully');
      } else {
        await createAlbum({
          title: formData.title,
          description: formData.description,
          coverImage: formData.coverImage,
          images: formData.images,
        });
        setSuccessMessage('Album created successfully');
      }

      await loadAlbums();
      // Refresh the snapshot so handleCloseModal's orphan diff is empty
      // — the new images are saved now, not orphans. Without this we'd
      // attempt to delete the just-saved photos on close.
      setOpenSnapshotImages([...formData.images]);
      // Close directly (bypass the orphan-confirm path entirely since
      // there are none).
      setShowModal(false);
      setEditingAlbum(null);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save album');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (albumId: string) => {
    if (!confirm('Are you sure you want to delete this album? All photos will be removed from the album.')) return;

    try {
      await deleteAlbum(albumId);
      setSuccessMessage('Album deleted successfully');
      await loadAlbums();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete album');
    }
  };

  const atImageLimit = imageLimit !== null && formData.images.length >= imageLimit;

  const handleImageUpload = (url: string) => {
    if (!url) return;
    if (imageLimit !== null && formData.images.length >= imageLimit) {
      setError(`Image limit reached (${imageLimit} per album on your plan). Upgrade to add more.`);
      return;
    }
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, url],
      // Auto-set cover image to the first uploaded image
      coverImage: prev.coverImage || url,
    }));
  };

  const handleImageRemove = (index: number) => {
    setFormData(prev => {
      const newImages = prev.images.filter((_, i) => i !== index);
      const removedUrl = prev.images[index];
      // If the removed image was the cover, reset cover to the new first image
      const newCover = prev.coverImage === removedUrl
        ? (newImages[0] || '')
        : prev.coverImage;
      return { ...prev, images: newImages, coverImage: newCover };
    });
  };

  const handleSetCover = (url: string) => {
    setFormData(prev => ({ ...prev, coverImage: url }));
  };

  if (isLoading) {
    return <div className="loading">Loading gallery...</div>;
  }

  const planLabel = plan === 'free' ? 'Free' : plan === 'club' ? 'Club' : 'Pro';

  return (
    <div className="gallery-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Photo Gallery</h1>
          <p className="page-subtitle">
            Manage photo albums
            {albumLimit !== null
              ? ` (${albums.length}/${albumLimit} albums on ${planLabel} plan)`
              : ` (${albums.length} album${albums.length !== 1 ? 's' : ''})`}
          </p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="btn btn-primary"
          disabled={atLimit}
          title={atLimit ? 'Upgrade to add more albums' : undefined}
        >
          New Album
        </button>
      </div>

      {atLimit && albumLimit !== null && hiddenCount === 0 && (
        <div className="limit-banner">
          <div className="limit-banner-content">
            <span className="limit-banner-icon">📸</span>
            <div>
              <strong>Album limit reached</strong>
              <p>{planLabel} plan allows up to {albumLimit} albums. Upgrade to add more.</p>
            </div>
          </div>
          <a href="/upgrade" className="btn btn-primary">Upgrade</a>
        </div>
      )}

      {hiddenCount > 0 && albumLimit !== null && (
        <div className="limit-banner">
          <div className="limit-banner-content">
            <span className="limit-banner-icon">👁️</span>
            <div>
              <strong>{hiddenCount} {hiddenCount === 1 ? 'album is' : 'albums are'} hidden from your public gallery</strong>
              <p>Your {planLabel} plan shows the latest {albumLimit} albums on the website. Older ones are kept here but won't appear to visitors. Upgrade to show all.</p>
            </div>
          </div>
          <a href="/upgrade" className="btn btn-primary">Upgrade</a>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {albums.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📸</div>
          <p>No photo albums yet — capture your club's best moments</p>
          <button onClick={() => handleOpenModal()} className="btn btn-primary">
            Create First Album
          </button>
        </div>
      ) : (
        <div className="gallery-grid">
          {albums.map((album) => {
            const thumb = album.coverImage || album.images[0] || '';
            return (
              <div key={album.albumId} className={`album-card ${album.hiddenFromPublic ? 'hidden-from-public' : ''}`}>
                <div className="album-cover">
                  {thumb ? (
                    <img src={thumb} alt={album.title} className="album-cover-img" />
                  ) : (
                    <div className="album-cover-placeholder">No Photos</div>
                  )}
                  <div className="album-image-count">
                    {album.imageCount ?? album.images.length} photo{(album.imageCount ?? album.images.length) !== 1 ? 's' : ''}
                  </div>
                  {album.hiddenFromPublic && (
                    <div className="hidden-badge" title="Not shown on your public gallery — upgrade to show all">
                      👁️ Hidden from public
                    </div>
                  )}
                </div>
                <div className="album-info">
                  <h3 className="album-title">{album.title}</h3>
                  {album.description && (
                    <p className="album-description">{album.description}</p>
                  )}
                  <div className="album-actions">
                    <button
                      onClick={() => handleOpenModal(album)}
                      className="btn btn-secondary btn-small"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(album.albumId)}
                      className="btn btn-danger btn-small"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" {...overlayClose(handleCloseModal)}>
          <div className="modal-content gallery-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingAlbum ? 'Edit Album' : 'New Album'}
              </h2>
              <button onClick={handleCloseModal} className="modal-close">
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="title" className="form-label">Album Title</label>
                <input
                  id="title"
                  type="text"
                  className="form-input"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g. Pre-Season Training 2025"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="description" className="form-label">Description (optional)</label>
                <textarea
                  id="description"
                  className="form-textarea"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  placeholder="A short description of this album..."
                />
              </div>

              <div className="form-group">
                <label className="form-label">Photos</label>

                {formData.images.length > 0 && (
                  <div className="album-images-grid">
                    {formData.images.map((img, index) => (
                      <div
                        key={index}
                        className={`album-image-item ${formData.coverImage === img ? 'is-cover' : ''}`}
                      >
                        <img src={img} alt={`Photo ${index + 1}`} className="album-image-thumb" />
                        <div className="album-image-overlay">
                          {formData.coverImage !== img && (
                            <button
                              type="button"
                              onClick={() => handleSetCover(img)}
                              className="image-set-cover"
                              title="Set as cover"
                            >
                              Cover
                            </button>
                          )}
                          {formData.coverImage === img && (
                            <span className="image-cover-badge">Cover</span>
                          )}
                          <button
                            type="button"
                            onClick={() => handleImageRemove(index)}
                            className="image-remove"
                            title="Remove photo"
                          >
                            &times;
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {atImageLimit ? (
                  <div className="limit-banner" style={{ marginTop: '12px' }}>
                    <div className="limit-banner-content">
                      <span className="limit-banner-icon">📸</span>
                      <div>
                        <strong>Image limit reached</strong>
                        <p>Your {planLabel} plan allows up to {imageLimit} images per album. Upgrade to add more.</p>
                      </div>
                    </div>
                    <a href="/upgrade" className="btn btn-primary btn-small">Upgrade</a>
                  </div>
                ) : (
                  <ImageUploader onUpload={handleImageUpload} label="Add Photos" multiple noPreview />
                )}
                <p className="form-hint">
                  {imageLimit !== null
                    ? `${formData.images.length}/${imageLimit} images. Select or drag multiple photos at once. Click "Cover" on any photo to make it the album cover.`
                    : 'Select or drag multiple photos at once. Click "Cover" on any photo to make it the album cover.'}
                </p>
              </div>

              {error && <div className="error-message">{error}</div>}

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : editingAlbum ? 'Update Album' : 'Create Album'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Gallery;
